<template>
  <div class="managed-instruction-details pa-2">
    <v-breadcrumbs :items="breadcrumbs" class="breadcrumbs" />
    <div class="d-flex mb-2 ml-0">
      <v-btn
        v-if="!isReadOnlyUser"
        color="primary mr-4"
        @click.stop="openTagAssignmentDialog()"
      >
        {{ $t("managedInstructions.tagAssignmentDialog.open") }}
      </v-btn>
    </div>
    <v-card outlined class="mb-3">
      <v-card-title class="py-3">
        <span>{{ $t("managedInstructions.instruction.assignedTags") }}</span>
      </v-card-title>
      <v-card-text>
        <div v-if="loading" class="grey--text text--darken-1">
          {{ $t("managedInstructions.instruction.loadingTags") }}
        </div>
        <div v-else class="d-flex flex-wrap" style="gap: 8px">
          <v-chip
            v-for="t in assignedTags"
            :key="t._id"
            small
            label
            class="mr-1"
          >
            {{ t.tag }}
          </v-chip>
          <div v-if="assignedTags.length === 0" class="grey--text">
            {{ $t("examination.details.noTags") }}
          </div>
        </div>
      </v-card-text>
    </v-card>
    <v-row dense class="mb-1">
      <v-col cols="12" sm="4">
        <v-card outlined>
          <v-card-text class="d-flex align-center">
            <v-icon class="mr-2">mdi-account-group</v-icon>
            <div>
              <div class="text-caption grey--text">
                {{ $t("managedInstructions.instruction.assignedEmployees") }}
              </div>
              <div class="text-h6">{{ eligibleCount }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card outlined>
          <v-card-text class="d-flex align-center">
            <v-icon class="mr-2">mdi-progress-clock</v-icon>
            <div>
              <div class="text-caption grey--text">
                {{ $t("managedInstructions.instruction.notCompleted") }}
              </div>
              <div class="text-h6">{{ inProgressCount }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card outlined>
          <v-card-text class="d-flex align-center">
            <v-icon class="mr-2">mdi-alert-circle</v-icon>
            <div>
              <div class="text-caption grey--text">
                {{ $t("managedInstructions.instruction.overdue") }}
              </div>
              <div class="text-h6">{{ overdueCount }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-tabs v-model="tab" background-color="transparent" class="mt-2">
      <v-tab> {{ $t("managedInstructions.instruction.allEmployees") }} </v-tab>
      <v-tab>{{ $t("managedInstructions.instruction.overdue") }}</v-tab>
    </v-tabs>
    <v-tabs-items v-model="tab" class="mt-3">
      <v-tab-item>
        <v-card outlined>
          <v-card-title class="d-flex align-center">
            <v-text-field
              v-model="searchEligible"
              single-line
              hide-details
              dense
              clearable
              :label="$t('general.search')"
              style="max-width: 370px"
            />
          </v-card-title>
          <v-data-table
            :headers="employeeHeaders"
            :items="eligibleEmployeesEnriched"
            :search="searchEligible"
            :loading="loading"
            item-key="_id"
            class="elevation-0"
          >
            <template v-slot:item.status="{ item }">
              <v-chip
                x-small
                label
                :class="statusChipClass(item.__assignmentStatus)"
              >
                {{ statusLabel(item.__assignmentStatus) }}
              </v-chip>
            </template>
            <template v-slot:item.dueDate="{ item }">
              <span v-if="item.__dueDate">
                {{ formatDate(item.__dueDate) }}
              </span>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:item.overdue="{ item }">
              <v-icon v-if="item.__isOverdue" small>mdi-alert</v-icon>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-if="isAdminOrMultiUser" v-slot:item.actions="{ item }">
              <v-tooltip v-if="item.__assignmentStatus !== 'SUCCESS'" bottom>
                <template v-slot:activator="{ on }">
                  <v-btn v-if="!isReadOnlyUser" icon small color="success" v-on="on" :loading="markingDone === item._id" @click="markDone(item)">
                    <v-icon small>mdi-check-circle</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("examination.markAsDone") }}</span>
              </v-tooltip>
              <v-icon v-else small color="success">mdi-check-circle</v-icon>
            </template>
            <template v-slot:no-data>{{
              $t("managedInstructions.instruction.noEmployeesFound")
            }}</template>
          </v-data-table>
        </v-card>
      </v-tab-item>
      <v-tab-item>
        <v-card outlined>
          <v-card-title class="d-flex align-center">
            <v-text-field
              v-model="searchOverdue"
              single-line
              hide-details
              dense
              clearable
              :label="$t('general.search')"
              style="max-width: 370px"
            />
          </v-card-title>
          <v-data-table
            :headers="employeeHeaders"
            :items="overdueEmployeesEnriched"
            :search="searchOverdue"
            :loading="loading"
            item-key="_id"
            class="elevation-0"
          >
            <template v-slot:item.status="{ item }">
              <v-chip
                x-small
                label
                :class="statusChipClass(item.__assignmentStatus)"
              >
                {{ statusLabel(item.__assignmentStatus) }}
              </v-chip>
            </template>
            <template v-slot:item.dueDate="{ item }">
              <span v-if="item.__dueDate">
                {{ formatDate(item.__dueDate) }}
              </span>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:item.overdue="{ item }">
              <v-icon v-if="item.__isOverdue" small>mdi-alert</v-icon>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-if="isAdminOrMultiUser" v-slot:item.actions="{ item }">
              <v-tooltip v-if="item.__assignmentStatus !== 'SUCCESS'" bottom>
                <template v-slot:activator="{ on }">
                  <v-btn v-if="!isReadOnlyUser" icon small color="success" v-on="on" :loading="markingDone === item._id" @click="markDone(item)">
                    <v-icon small>mdi-check-circle</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("examination.markAsDone") }}</span>
              </v-tooltip>
              <v-icon v-else small color="success">mdi-check-circle</v-icon>
            </template>
            <template v-slot:no-data>
              {{
                $t("managedInstructions.instruction.noOverdueEmployees")
              }}</template
            >
          </v-data-table>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
    <examination-tag-assignment-dialog
      ref="tagAssignmentDialog"
      :examination-id="examinationId"
      @saved="loadAll()"
    />
  </div>
</template>
<script>
import i18n from "../../i18n";
import ExaminationTagAssignmentDialog from "@/views/examination/examinationTagAssignmentDialog.vue";
export default {
  name: "ExaminationDetailsPage",
  components: {
    ExaminationTagAssignmentDialog,
  },
  props: { examinationId: { type: String, required: true } },
  data: () => ({
    tab: 0,
    loading: false,
    searchEligible: "",
    searchOverdue: "",
    examination: null,
    assignedTags: [],
    eligibleEmployees: [],
    employeeAssignments: [],
    isAdminOrMultiUser: false,
    markingDone: null,
    stats: {
      eligibleCount: 0,
      inProgressCount: 0,
      overdueCount: 0,
    },
  }),
  computed: {
    breadcrumbs() {
      return [
        {
          text: i18n.t("sidebar.examination"),
          disabled: false,
          href: "/app/examination",
        },
        {
          text: this.$route.params.examinationName,
          disabled: true,
        },
      ];
    },
    employeeHeaders() {
      return [
        {
          text: this.$t("examination.tableHeader.name"),
          value: "name",
        },
        {
          text: this.$t("examination.tableHeader.email"),
          value: "email",
        },
        {
          text: this.$t("examination.tableHeader.status"),
          value: "status",
          align: "center",
        },
        {
          text: this.$t("examination.tableHeader.overdue"),
          value: "overdue",
          align: "center",
          sortable: false,
        },
        ...(this.isAdminOrMultiUser
          ? [{ text: "", value: "actions", align: "right", sortable: false }]
          : []),
      ];
    },
    assignmentByEmployeeId() {
      const map = new Map();
      for (const a of this.employeeAssignments) {
        const employeeId =
          typeof a.employee === "string" ? a.employee : a.employee?._id;
        if (employeeId) map.set(employeeId, a);
      }
      return map;
    },
    eligibleEmployeesEnriched() {
      const now = new Date();
      return this.eligibleEmployees.map((e) => {
        const id = e._id;
        const a = this.assignmentByEmployeeId.get(id);
        const status = a?.status || null;
        const overdueDate = a?.overdueDate ? new Date(a.overdueDate) : null;
        const isDone = status === "SUCCESS";
        const isOverdue =
          !!overdueDate && !isDone && overdueDate.getTime() <= now.getTime();
        return {
          ...e,
          __assignmentStatus: status,
          __assignmentId: a?._id || null,
          __dueDate: overdueDate,
          __isOverdue: isOverdue,
        };
      });
    },
    overdueEmployeesEnriched() {
      return this.eligibleEmployeesEnriched.filter((e) => e.__isOverdue);
    },
    eligibleCount() {
      return this.eligibleEmployees.length;
    },
    inProgressCount() {
      return this.eligibleEmployeesEnriched.filter(
        (e) => !!e.__assignmentStatus && e.__assignmentStatus !== "SUCCESS",
      ).length;
    },
    overdueCount() {
      return this.overdueEmployeesEnriched.length;
    },
  },
  async created() {
    const user = await this.$store.dispatch("getUserInfo");
    // "Owner" is shown in the UI as "Administrator" (see users/formattedRole.vue),
    // so it must be treated as an admin here too.
    this.isAdminOrMultiUser =
      user?.role === "Owner" ||
      user?.role === "Admin" ||
      user?.role === "MultiUser";
    this.loadAll();
  },
  methods: {
    openEditDialog() {
      this.$nextTick(() => this.$refs.editDialog.openDialog());
    },
    async loadAll() {
      this.loading = true;
      try {
        const { data } = await this.$store.dispatch(
          "getExaminationAssignmentOverview",
          this.examinationId,
        );
        this.examination = data.examination || null;

        this.assignedTags = data.assignedTags || [];
        this.stats = data.stats || {
          eligibleCount: 0,
          inProgressCount: 0,
          overdueCount: 0,
        };
        this.eligibleEmployees = (data.employees || []).map((e) => ({
          _id: e._id,
          name: e.name,
          email: e.email,
        }));
        this.employeeAssignments = (data.employees || [])
          .filter((e) => !!e.assignment)
          .map((e) => ({
            _id: e.assignment._id,
            employee: e._id,
            status: e.assignment.status,
            overdueDate: e.assignment.overdueDate,
            lastEmailScheduledFor: e.assignment.lastEmailScheduledFor,
          }));
      } finally {
        this.loading = false;
      }
    },
    async markDone(item) {
      if (!item.__assignmentId) return;
      this.markingDone = item._id;
      try {
        await this.$store.dispatch("markExaminationEmployeeDone", {
          assignmentId: item.__assignmentId,
          employeeId: item._id,
        });
        this.$store.commit("CREATE_SNACKBAR", this.$t("examination.markedAsDone"));
        await this.loadAll();
      } finally {
        this.markingDone = null;
      }
    },
    openTagAssignmentDialog() {
      this.$nextTick(() => this.$refs.tagAssignmentDialog.openDialog());
    },
    statusLabel(status) {
      if (!status) return "not assigned";
      if (status === "ASSIGNED") return "assigned";
      if (status === "SUCCESS") return "done";
      return status;
    },
    statusChipClass(status) {
      if (status === "SUCCESS") return "green lighten-4";
      if (status === "ASSIGNED") return "grey lighten-3";
      return "grey lighten-4";
    },
    formatDate(d) {
      if (!d) return "";
      const date = d instanceof Date ? d : new Date(d);
      return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
    },
  },
};
</script>
<style>
.managed-instruction-details .v-tab {
  text-transform: none;
}
</style>
